import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { authApi } from '../api/auth'
import { usersApi } from '../api/users'
import { Layout } from '../components/Layout'
import { APP_VERSION, APP_COMMIT } from '../version'
import { deletionCompletedDespiteError } from '../utils/accountDeletion'

// Subscription statuses that already grant full access. Mirrors the
// backend single source of truth (app/core/entitlements.py). Used here
// only to hide the invite-code row from users who are already entitled.
const ENTITLED_STATUSES = new Set(['founding', 'founding_comp', 'active'])

export default function SettingsPage() {
  const { user, token, logout, patchUser } = useAuth()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()

  function handleLogout() {
    logout()
    navigate('/auth', { replace: true })
  }

  const showInviteCode = !ENTITLED_STATUSES.has(user?.subscription_status)

  return (
    <Layout>
      <div className="pb-24 space-y-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Settings</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Account and preferences</p>
        </div>

        <ProfileSection user={user} token={token} />
        {showInviteCode && <InviteCodeSection token={token} patchUser={patchUser} />}
        <AppearanceSection theme={theme} setTheme={setTheme} />
        <OuraSection />
        <PrivacySection user={user} token={token} patchUser={patchUser} />
        <AccountSection onLogout={handleLogout} token={token} />
        <DisclaimerSection />
      </div>
    </Layout>
  )
}

function InviteCodeSection({ token, patchUser }) {
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [granted, setGranted] = useState(false)
  const [error, setError] = useState('')

  async function apply() {
    const value = code.trim()
    if (!value || submitting) return
    setSubmitting(true)
    setError('')
    try {
      await authApi.applyCode(value, token)
      // Re-fetch the user so the paywall / entitlement state updates
      // immediately — no logout required.
      try {
        const fresh = await authApi.me(token)
        patchUser(fresh)
      } catch {
        // Fall back to a minimal local patch if the refetch hiccups;
        // the grant already succeeded server-side.
        patchUser({ subscription_status: 'founding_comp' })
      }
      setGranted(true)
    } catch (err) {
      setError(err?.message || "That code isn't valid.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Section title="Invite Code">
      <div className="px-4 py-3.5">
        {granted ? (
          <p className="text-sm text-haven-600 dark:text-haven-400 font-medium">
            Access granted — you're part of the founding cohort.
          </p>
        ) : (
          <>
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-2.5">
              Have an invite code? Enter it to join the founding cohort.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                style={{ textTransform: 'uppercase' }}
                className="flex-1 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-haven-500"
              />
              <button
                onClick={apply}
                disabled={submitting || !code.trim()}
                className="px-4 py-2 text-xs font-semibold bg-haven-600 hover:bg-haven-700 disabled:opacity-50 text-white rounded-lg transition-colors shrink-0"
              >
                {submitting ? '…' : 'Apply'}
              </button>
            </div>
            {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
          </>
        )}
      </div>
    </Section>
  )
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</h3>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {children}
      </div>
    </div>
  )
}

function Row({ label, children, description }) {
  return (
    <div className="flex items-center justify-between px-4 py-3.5 gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {description && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{description}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

function ProfileSection({ user, token }) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(user?.full_name || '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  async function save() {
    setSaving(true)
    setSaveError('')
    try {
      await authApi.updateProfile({ full_name: name }, token)
      setEditing(false)
    } catch {
      setSaveError('Save failed. Try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section title="Profile">
      <Row label="Email">
        <span className="text-sm text-slate-500 dark:text-slate-400">{user?.email || '—'}</span>
      </Row>
      <Row label="Name">
        {editing ? (
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-haven-500 w-36"
              />
              <button
                onClick={save}
                disabled={saving}
                className="px-3 py-1.5 text-xs font-semibold bg-haven-600 hover:bg-haven-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {saving ? '…' : 'Save'}
              </button>
            </div>
            {saveError && <p className="text-xs text-red-500">{saveError}</p>}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">{name || '—'}</span>
            <button
              onClick={() => setEditing(true)}
              className="text-xs font-medium text-haven-600 dark:text-haven-400"
            >
              Edit
            </button>
          </div>
        )}
      </Row>
      <Row label="User ID" description="Internal identifier">
        <span className="text-xs font-mono text-slate-400 dark:text-slate-500 truncate max-w-[10rem]">{user?.id || '—'}</span>
      </Row>
    </Section>
  )
}

const THEME_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

function AppearanceSection({ theme, setTheme }) {
  return (
    <Section title="Appearance">
      <Row label="Theme">
        <div className="flex gap-1 rounded-xl bg-slate-100 dark:bg-slate-800 p-1">
          {THEME_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setTheme(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                theme === opt.value
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </Row>
    </Section>
  )
}

// Switch styled from the existing slate/haven tokens already used on
// this page — no new colors, no new type.
function Toggle({ value, onChange, disabled = false }) {
  return (
    <button
      role="switch"
      aria-checked={value}
      disabled={disabled}
      onClick={() => { if (!disabled) onChange(!value) }}
      className={`relative w-[42px] h-6 rounded-full transition-colors shrink-0 disabled:opacity-60 ${
        value ? 'bg-haven-600' : 'bg-slate-200 dark:bg-slate-700'
      }`}
    >
      <span
        className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-all ${
          value ? 'left-[21px]' : 'left-[3px]'
        }`}
      />
    </button>
  )
}

// Research-consent control. The privacy policy tells users they can
// review or change this choice in Settings — this section is what makes
// that sentence true. research_consent is tri-state on the server
// (true / false / NULL = never asked). NULL renders OFF, matching the
// backend's fail-closed reading: "we never asked" is not a yes. The
// first interaction writes an explicit boolean, resolving the NULL.
function PrivacySection({ user, token, patchUser }) {
  const [saving, setSaving] = useState(false)
  const consent = user?.research_consent === true
  const neverAsked = user?.research_consent == null

  async function toggle(next) {
    if (saving) return
    setSaving(true)
    patchUser({ research_consent: next })
    try {
      await usersApi.setConsent(next, token)
    } catch {
      // The server still holds the previous value; the next /auth/me
      // refresh reconciles the toggle.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section title="Privacy">
      <Row
        label="Research use"
        description={
          'Lets Haiven use your de-identified signal for research and product improvement. Also required to include written entries in anything you choose to share.' +
          (neverAsked ? ' Not set yet.' : '')
        }
      >
        <Toggle value={consent} onChange={toggle} disabled={saving} />
      </Row>
    </Section>
  )
}

function OuraSection() {
  return (
    <Section title="Integrations">
      <Row label="Wearables">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Manage wearable connections under Wearables.
        </span>
      </Row>
    </Section>
  )
}

function AccountSection({ onLogout, token }) {
  const [confirming, setConfirming] = useState(false)
  // In-app account deletion (App Store Guideline 5.1.1(v)) — mirrors
  // the flow in Profile's Account card so it's easy to find from
  // either surface.
  //
  // Two confirm screens, not one: 0 = idle, 1 = "Delete your account?",
  // 2 = "This is permanent." Only the step-2 button calls the API.
  // Cancel on EITHER step returns straight to 0 — a cancel is never a
  // step backwards into the flow it is trying to leave.
  const [deleteStep, setDeleteStep] = useState(0)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  function cancelDelete() {
    setDeleteStep(0)
    setDeleteError('')
  }

  async function handleDelete() {
    if (deleting) return
    setDeleteError('')
    setDeleting(true)
    try {
      await authApi.deleteAccount(token)
      onLogout()
    } catch (err) {
      // 401/404 = the account is already gone (client retry replayed
      // the DELETE after a lost response) — finish the logout instead
      // of showing an error over a successful deletion.
      if (deletionCompletedDespiteError(err)) {
        onLogout()
        return
      }
      setDeleting(false)
      setDeleteError('Something went wrong deleting your account. Please try again.')
    }
  }

  return (
    <Section title="Account">
      <div className="px-4 py-3.5">
        {confirming ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-600 dark:text-slate-400">Sign out of Haiven?</p>
            <div className="flex gap-2">
              <button
                onClick={onLogout}
                className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              >
                Sign Out
              </button>
              <button
                onClick={() => setConfirming(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            className="text-sm font-medium text-red-500 dark:text-red-400"
          >
            Sign Out
          </button>
        )}
      </div>
      <div className="px-4 py-3.5">
        {deleteStep > 0 ? (
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {deleteStep === 1 ? 'Delete your account?' : 'This is permanent.'}
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {deleteStep === 1
                ? "This can't be undone. Your login and personal information are permanently deleted."
                : "Once you confirm, your account can't be recovered."}
            </p>
            <div className="flex gap-2">
              {deleteStep === 1 ? (
                <button
                  onClick={() => setDeleteStep(2)}
                  className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-60"
                >
                  {deleting ? 'Deleting…' : 'Delete My Account'}
                </button>
              )}
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
            {deleteError && (
              <p className="text-xs text-red-500">{deleteError}</p>
            )}
          </div>
        ) : (
          <button
            onClick={() => setDeleteStep(1)}
            className="text-sm text-slate-500 dark:text-slate-400"
          >
            Delete account
          </button>
        )}
      </div>
    </Section>
  )
}

function DisclaimerSection() {
  return (
    <div className="px-2 py-4 text-xs text-slate-400 dark:text-slate-600 text-center leading-relaxed">
      Haiven does not diagnose, treat, cure, or provide psychotherapy. It measures
      conditions — not outcomes. If you are in crisis, please contact a qualified
      professional or call 988 (Suicide &amp; Crisis Lifeline).
      {/* Build identity — answers "am I on the latest?" when debugging
          stale PWA installs. Stamped at build time (vite define). */}
      <div className="mt-3 opacity-70">
        Haiven v{APP_VERSION} · {APP_COMMIT}
      </div>
    </div>
  )
}
